import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, GBP, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PaymentBadge, StatusBadge } from "@/components/StatusBadge";
import { Calendar, ArrowRight, BookOpen, Award, Clock, PlayCircle, Download } from "lucide-react";

const ACCESS = ["paid_in_full", "comped", "sponsored"];

function countdown(dateStr) {
  if (!dateStr) return null;
  const days = Math.ceil((new Date(dateStr) - new Date()) / 86400000);
  return days;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [enrolments, setEnrolments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/my/enrolments").then((r) => setEnrolments(r.data)).finally(() => setLoading(false));
  }, []);

  const upcoming = enrolments.filter((e) => e.status === "enrolled" || e.status === "approved");
  const recent = enrolments
    .filter((e) => ACCESS.includes(e.payment_status) && e.status !== "refunded")
    .sort((a, b) => new Date(b.last_studied_at || b.content_accessed_at || 0) - new Date(a.last_studied_at || a.content_accessed_at || 0))[0];
  const recentDone = recent && recent.certificate_number;

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10" data-testid="learner-dashboard">
      <p className="text-xs tracking-label uppercase font-semibold text-[var(--accent)]">My learning</p>
      <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight mt-1 mb-8">Hi {user?.name?.split(" ")[0]} 👋</h1>

      {recent && (
        <div data-testid="resume-banner" className="mb-10 border border-[var(--ink)] bg-[var(--ink)] text-white overflow-hidden flex flex-col sm:flex-row sm:items-center gap-4 p-4 sm:p-5">
          <img src={recent.course?.image} alt="" className="w-full sm:w-32 h-32 sm:h-20 object-cover" />
          <div className="flex-1">
            <p className="text-[11px] uppercase tracking-label font-semibold text-[var(--accent)]">{recentDone ? "🎉 Course completed" : "Pick up where you left off"}</p>
            <h2 className="font-display font-bold text-xl mt-0.5">{recent.course_title}</h2>
            {recentDone ? (
              <p className="text-sm text-white/70 mt-1">Certificate {recent.certificate_number} — download and share it below.</p>
            ) : recent.progress_total > 0 ? (
              <p className="text-sm text-white/70 mt-1">{recent.progress_pct}% complete · {recent.progress_done}/{recent.progress_total} lessons</p>
            ) : null}
          </div>
          {recentDone ? (
            <a href={`${API}/certificates/${recent.certificate_number}/pdf`} target="_blank" rel="noreferrer" data-testid="resume-download-certificate-btn" className="inline-flex items-center justify-center gap-2 bg-[var(--accent)] text-white font-semibold px-6 py-3.5 hover:bg-[var(--accent-hover)] whitespace-nowrap">
              <Download size={18} /> Download certificate
            </a>
          ) : (
            <Link to={`/learn/${recent.course?.id}`} data-testid="resume-last-course-btn" className="inline-flex items-center justify-center gap-2 bg-[var(--accent)] text-white font-semibold px-6 py-3.5 hover:bg-[var(--accent-hover)] whitespace-nowrap">
              <PlayCircle size={18} /> {recent.progress_done > 0 ? "Resume course" : "Start course"}
            </Link>
          )}
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-4 mb-10">
        {[
          { icon: BookOpen, label: "My courses", value: enrolments.length },
          { icon: Clock, label: "Active enrolments", value: upcoming.length },
          { icon: Award, label: "Certificates", value: enrolments.filter((e) => e.certificate_number).length },
        ].map((s) => (
          <div key={s.label} className="border border-[var(--line)] bg-white p-5 flex items-center justify-between" data-testid={`stat-${s.label}`}>
            <div><div className="font-display font-extrabold text-3xl">{s.value}</div><div className="text-sm text-[var(--ink-2)]">{s.label}</div></div>
            <s.icon className="text-[var(--accent)]" size={26} />
          </div>
        ))}
      </div>

      <h2 className="font-display font-bold text-xl mb-4">My courses</h2>
      {loading ? <p className="text-[var(--ink-2)]">Loading…</p> : enrolments.length === 0 ? (
        <div className="border border-[var(--line)] bg-white p-10 text-center" data-testid="no-enrolments">
          <p className="text-[var(--ink-2)] mb-4">You haven't enrolled in any courses yet.</p>
          <Link to="/courses" className="inline-flex items-center gap-2 bg-[var(--accent)] text-white font-semibold px-5 py-3 hover:bg-[var(--accent-hover)]">Browse courses <ArrowRight size={16} /></Link>
        </div>
      ) : (
        <div className="space-y-4">
          {enrolments.map((e) => {
            const days = countdown(e.course?.start_date);
            const hasAccess = ACCESS.includes(e.payment_status) && e.status !== "refunded";
            return (
              <div key={e.id} data-testid={`enrolment-row-${e.id}`} className="flex flex-col sm:flex-row gap-4 border border-[var(--line)] bg-white p-4 hover:border-[var(--ink)] transition-colors">
                <img src={e.course?.image} alt="" className="w-full sm:w-40 aspect-[16/10] object-cover" />
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <StatusBadge status={e.status} /><PaymentBadge status={e.payment_status} />
                  </div>
                  <h3 className="font-display font-bold text-lg">{e.course_title}</h3>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-[var(--ink-2)] mt-1">
                    <span className="inline-flex items-center gap-1.5"><Calendar size={14} /> Starts {e.course?.start_date}</span>
                    {days > 0 && <span className="text-[var(--accent)] font-semibold">{days} days to go</span>}
                  </div>
                  <div className="text-sm mt-2">Paid {GBP(e.amount_paid)} of {GBP(e.total_price)}</div>
                  {e.progress_total > 0 && !e.certificate_number && (
                    <div className="mt-3" data-testid={`progress-nudge-${e.id}`}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="font-semibold text-[var(--accent)]">
                          {e.progress_pct >= 100
                            ? "All lessons complete — your certificate is being prepared"
                            : e.progress_pct === 0
                              ? "You've started — jump back in to earn your certificate"
                              : `You're ${e.progress_pct}% through — finish to earn your certificate`}
                        </span>
                        <span className="text-[var(--ink-2)]">{e.progress_done}/{e.progress_total}</span>
                      </div>
                      <div className="h-1.5 bg-[var(--line)]"><div className="h-1.5 bg-[var(--accent)]" style={{ width: `${e.progress_pct}%` }} /></div>
                    </div>
                  )}
                  {e.certificate_number && (
                    <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#1B5E20]" data-testid={`completed-badge-${e.id}`}><Award size={13} /> Completed · certificate earned</div>
                  )}
                  <div className="mt-4 flex flex-wrap gap-2">
                    {e.certificate_number ? (
                      <a href={`${API}/certificates/${e.certificate_number}/pdf`} target="_blank" rel="noreferrer" data-testid={`download-certificate-${e.id}`} className="inline-flex items-center gap-2 bg-[var(--accent)] text-white font-semibold px-4 py-2 text-sm hover:bg-[var(--accent-hover)]">
                        <Download size={16} /> Download certificate
                      </a>
                    ) : hasAccess ? (
                      <Link to={`/learn/${e.course?.id}`} data-testid={`continue-learning-${e.id}`} className="inline-flex items-center gap-2 bg-[var(--accent)] text-white font-semibold px-4 py-2 text-sm hover:bg-[var(--accent-hover)]">
                        <PlayCircle size={16} /> {e.progress_done > 0 ? "Continue learning" : "Start learning"}
                      </Link>
                    ) : null}
                    <Link to={`/enrolment/${e.id}`} data-testid={`enrolment-details-${e.id}`} className="inline-flex items-center gap-2 border border-[var(--line)] px-4 py-2 text-sm font-semibold hover:border-[var(--ink)] transition-colors">
                      View details <ArrowRight size={15} />
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
